import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/brand";

export default function Login() {
  const { login, setUser } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState("password");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [otpMode, setOtpMode] = useState("login");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpHint, setOtpHint] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const goAfter = (u) => {
    toast.success(`Welcome, ${(u.name || "").split(" ")[0] || "there"}!`);
    nav(u.role === "customer" ? "/account" : "/admin");
  };

  const submitPassword = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { const u = await login(mobile, password); goAfter(u); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  const requestOtp = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone, mode: otpMode, name });
      setOtpSent(true);
      setOtpHint(data.otp ? `Your OTP is: ${data.otp}` : "");
      toast.success("OTP generated");
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Could not send OTP"); }
    finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, otp, mode: otpMode, name });
      localStorage.setItem("ln_token", data.token);
      setUser(data.user);
      goAfter(data.user);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Verification failed"); }
    finally { setLoading(false); }
  };

  const switchOtpMode = (m) => { setOtpMode(m); setOtpSent(false); setOtp(""); setOtpHint(""); setErr(""); };
  const changeNumber = () => { setOtpSent(false); setOtp(""); setOtpHint(""); setErr(""); };

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button type="button" onClick={() => { setTab(id); setErr(""); }} data-testid={`login-tab-${id}`}
      className={`flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full text-sm font-600 transition-colors ${tab === id ? "bg-forest text-cream" : "text-forest hover:bg-forest/5"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 leaf-pattern">
      <div className="w-full max-w-md bg-white rounded-[28px] soft-shadow p-8">
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO_URL} alt="LIVENEAT logo" className="h-32 w-auto object-contain mb-3" />
          <h1 className="font-heading text-3xl text-forest">Welcome Back</h1>
          <p className="text-sm text-brown/70">Sign in to your LIVENEAT account</p>
        </div>

        <div className="flex gap-2 bg-sand rounded-full p-1 mb-6">
          <TabBtn id="password" icon={KeyRound} label="Password" />
          <TabBtn id="otp" icon={Smartphone} label="Mobile OTP" />
        </div>

        {tab === "password" && (
          <form onSubmit={submitPassword} className="space-y-4">
            <div><Label className="text-sm">Mobile Number</Label><Input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} required className="rounded-xl bg-sand" data-testid="login-mobile" placeholder="10-digit mobile number" /></div>
            <div><Label className="text-sm">Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-xl bg-sand" data-testid="login-password" /></div>
            {err && <p className="text-sm text-terracotta" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-full h-12 bg-forest hover:bg-earth text-cream" data-testid="login-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}</Button>
          </form>
        )}

        {tab === "otp" && (
          <div className="space-y-4">
            <div className="flex gap-2 text-sm">
              <button type="button" onClick={() => switchOtpMode("login")} data-testid="otp-mode-login"
                className={`flex-1 h-10 rounded-xl font-500 transition-colors ${otpMode === "login" ? "bg-forest/10 text-forest border border-forest/30" : "bg-sand text-brown/70"}`}>Existing customer</button>
              <button type="button" onClick={() => switchOtpMode("signup")} data-testid="otp-mode-signup"
                className={`flex-1 h-10 rounded-xl font-500 transition-colors ${otpMode === "signup" ? "bg-forest/10 text-forest border border-forest/30" : "bg-sand text-brown/70"}`}>New customer</button>
            </div>

            {!otpSent ? (
              <form onSubmit={requestOtp} className="space-y-4">
                {otpMode === "signup" && (
                  <div><Label className="text-sm">Full Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-xl bg-sand" data-testid="otp-name" placeholder="Your name" /></div>
                )}
                <div><Label className="text-sm">Mobile Number</Label><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required className="rounded-xl bg-sand" data-testid="otp-phone" placeholder="10-digit mobile number" /></div>
                {err && <p className="text-sm text-terracotta" data-testid="login-error">{err}</p>}
                <Button type="submit" disabled={loading} className="w-full rounded-full h-12 bg-forest hover:bg-earth text-cream" data-testid="otp-request-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send OTP"}</Button>
              </form>
            ) : (
              <form onSubmit={verifyOtp} className="space-y-4">
                {otpHint && (
                  <div className="rounded-xl bg-mustard/20 border border-mustard/50 px-4 py-3 text-sm text-ink" data-testid="otp-hint">
                    <span className="font-700">{otpHint}</span>
                    <p className="text-xs text-brown/70 mt-1">SMS delivery isn't configured yet, so your code is shown here. Enter it below to continue.</p>
                  </div>
                )}
                <div><Label className="text-sm">Enter OTP</Label><Input inputMode="numeric" value={otp} onChange={(e) => setOtp(e.target.value)} required className="rounded-xl bg-sand tracking-[0.4em] text-center text-lg" maxLength={6} data-testid="otp-code" placeholder="6-digit code" /></div>
                {err && <p className="text-sm text-terracotta" data-testid="login-error">{err}</p>}
                <Button type="submit" disabled={loading} className="w-full rounded-full h-12 bg-forest hover:bg-earth text-cream" data-testid="otp-verify-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (otpMode === "signup" ? "Create Account" : "Verify & Sign In")}</Button>
                <button type="button" onClick={changeNumber} className="w-full text-sm text-forest underline" data-testid="otp-change-number">Use a different number</button>
              </form>
            )}
          </div>
        )}

        <p className="text-sm text-center text-brown/70 mt-5">No account? <Link to="/register" className="text-forest font-500 underline">Create one</Link></p>
      </div>
    </div>
  );
}
